import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Globe, Users, Award, Zap } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { services, industries, testimonials } from '../mock';
import * as Icons from 'lucide-react';

const Home = () => {
  const whyChooseUs = [
    { icon: 'Award', title: 'Proven Expertise', description: 'Years of experience in ERP and automation' },
    { icon: 'Globe', title: 'Global Presence', description: 'Serving clients across US, Middle East, and Asia' },
    { icon: 'Users', title: 'Certified Consultants', description: 'Team of certified technology experts' },
    { icon: 'Zap', title: 'Custom Development', description: 'Tailored solutions for your unique needs' },
    { icon: 'CheckCircle2', title: '360° Automation', description: 'End-to-end business automation services' },
    { icon: 'Award', title: 'Ongoing Support', description: 'Dedicated post-implementation assistance' }
  ];

  const stats = [
    { number: '500+', label: 'Clients Served' },
    { number: '1000+', label: 'Projects Completed' },
    { number: '15+', label: 'Countries' },
    { number: '98%', label: 'Client Satisfaction' }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1644325349124-d1756b79dd42" 
            alt="Digital Transformation" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-blue-900/95 via-blue-800/90 to-purple-900/95"></div>
        </div>

        {/* Animated Elements */}
        <div className="absolute inset-0 z-0 opacity-20">
          <div className="absolute top-20 left-20 w-72 h-72 bg-blue-400 rounded-full filter blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-purple-400 rounded-full filter blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        </div>

        {/* Content */}
        <div className="container mx-auto px-4 z-10 relative">
          <div className="max-w-4xl mx-auto text-center text-white">
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight animate-fade-in">
              Transform Your Business with <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-300 to-purple-300">Next-Gen Automation with Power of AI</span>
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-gray-200 animate-fade-in" style={{ animationDelay: '0.2s' }}>
              From ERP Implementation to Intelligent Integrations — We Make Your Business 10x More Productive
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in" style={{ animationDelay: '0.4s' }}>
              <Link to="/contact">
                <Button size="lg" className="bg-white text-blue-900 hover:bg-gray-100 text-lg px-8 py-6 shadow-xl hover:shadow-2xl transition-all">
                  Get Started <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/services">
                <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-blue-900 text-lg px-8 py-6 transition-all">
                  Explore Services
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-blue-600 mb-2">{stat.number}</div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">Why Choose Gemini Tech Labs</h2>
            <div className="max-w-4xl mx-auto text-left space-y-4 mb-12">
              <p className="text-lg text-gray-700 leading-relaxed">
                In today's rapidly evolving business landscape, digital transformation is no longer optional—it's essential for survival and growth. At Gemini Tech Labs, we understand that every business faces unique challenges in managing operations, streamlining workflows, and scaling efficiently. That's why we've dedicated ourselves to becoming more than just a technology provider; we're your strategic partner in achieving operational excellence.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                With over a decade of combined expertise in ERP implementation, system integration, and business process automation, our certified consultants have successfully transformed hundreds of businesses across the United States, Middle East, and Southeast Asia. We don't just implement software—we understand your business processes, identify bottlenecks, and design solutions that drive real, measurable results.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                What sets us apart is our commitment to long-term partnerships. From initial consultation and requirement analysis through implementation, training, and ongoing support, we're with you every step of the way. Our 98% client satisfaction rate speaks to our dedication to delivering solutions that not only meet but exceed expectations, helping businesses achieve up to 10x productivity improvements and significant cost reductions.
              </p>
            </div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {whyChooseUs.map((item, index) => {
              const IconComponent = Icons[item.icon];
              return (
                <Card key={index} className="border-none shadow-lg hover:shadow-xl transition-all hover:-translate-y-1 bg-white">
                  <CardContent className="p-8">
                    <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center mb-4">
                      <IconComponent className="h-7 w-7 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{item.title}</h3>
                    <p className="text-gray-600">{item.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Services Overview Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">Our Core Services</h2>
            <div className="max-w-4xl mx-auto text-left space-y-4 mb-12">
              <p className="text-lg text-gray-700 leading-relaxed">
                Our comprehensive suite of services is designed to address every aspect of your digital transformation journey. Whether you're a startup looking to establish scalable systems from day one, or an established enterprise seeking to modernize legacy infrastructure, we have the expertise and solutions to guide you.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                As certified partners for leading ERP platforms including Zoho, Odoo, Microsoft Dynamics 365, QuickBooks, and Tally, we provide end-to-end implementation services that go beyond basic setup. Our approach includes thorough business process analysis, custom workflow design, data migration, system integration, employee training, and continuous optimization to ensure your technology investment delivers maximum ROI.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                From intelligent automation and third-party integrations to mobile app development and digital marketing, our services are interconnected and designed to work seamlessly together, creating a unified digital ecosystem that drives efficiency, reduces costs, and enables data-driven decision-making across your entire organization.
              </p>
            </div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.slice(0, 3).map((service) => {
              const IconComponent = Icons[service.icon];
              return (
                <Card key={service.id} className="border-none shadow-lg hover:shadow-xl transition-all group overflow-hidden">
                  <div className="relative h-48 overflow-hidden">
                    <img src={service.image} alt={service.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                    <div className="absolute inset-0 bg-gradient-to-t from-blue-900/80 to-transparent"></div>
                  </div>
                  <CardContent className="p-8">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center mb-4">
                      <IconComponent className="h-6 w-6 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{service.title}</h3>
                    <p className="text-gray-600 mb-4">{service.description}</p>
                    <Link to="/services" className="text-blue-600 hover:text-blue-700 font-semibold inline-flex items-center group">
                      Learn More <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                    </Link>
                  </CardContent>
                </Card>
              );
            })}
          </div>
          <div className="text-center mt-12">
            <Link to="/services">
              <Button size="lg" className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-8">
                View All Services <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* AI Section */}
      <section className="py-20 bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-96 h-96 bg-purple-400 rounded-full filter blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-blue-400 rounded-full filter blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-5xl mx-auto text-center mb-12">
            <div className="inline-block mb-4">
              <span className="px-4 py-2 bg-purple-500/30 backdrop-blur-sm rounded-full text-sm font-semibold border border-purple-300/50">
                Artificial Intelligence & Business Automation
              </span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-6">Transform Your Business with AI That Actually Works</h2>
            <p className="text-xl mb-4 text-purple-100">From AI Assistants to Autonomous Business Agents</p>
            <p className="text-base md:text-lg mb-8 text-gray-300 max-w-4xl mx-auto">
              Artificial Intelligence is moving beyond chatbots and content generation. At Gemini High Tech Systems, we help businesses integrate AI directly into their day-to-day operations — connecting AI with business data, ERP systems, CRM platforms, documents, websites, WhatsApp, voice communication and internal knowledge.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            <Card className="border-none shadow-lg bg-white/10 backdrop-blur-sm hover:bg-white/20 transition-all">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 bg-purple-500/30 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-lg font-bold mb-2">AI + Your Data</h3>
                <p className="text-sm text-purple-100">Connect AI with your ERP, CRM, documents and business systems</p>
              </CardContent>
            </Card>
            <Card className="border-none shadow-lg bg-white/10 backdrop-blur-sm hover:bg-white/20 transition-all">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 bg-blue-500/30 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Zap className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-lg font-bold mb-2">Intelligent Automation</h3>
                <p className="text-sm text-purple-100">Automate workflows, customer service, finance and operations</p>
              </CardContent>
            </Card>
            <Card className="border-none shadow-lg bg-white/10 backdrop-blur-sm hover:bg-white/20 transition-all">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 bg-indigo-500/30 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Award className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-lg font-bold mb-2">24×7 Availability</h3>
                <p className="text-sm text-purple-100">AI agents, WhatsApp automation and voice assistants</p>
              </CardContent>
            </Card>
          </div>
          <div className="text-center">
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/ai-solutions">
                <Button size="lg" className="bg-white text-purple-900 hover:bg-gray-100 text-lg px-8 py-6 shadow-xl">
                  Explore AI Solutions <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/ai-products">
                <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-purple-900 text-lg px-8 py-6">
                  View AI Products
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Industries Section */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-purple-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Industries We Serve</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Specialized solutions tailored for your industry
            </p>
          </div>
          <div className="grid md:grid-cols-3 lg:grid-cols-6 gap-6">
            {industries.map((industry) => {
              const IconComponent = Icons[industry.icon];
              return (
                <Card key={industry.id} className="border-none shadow-md hover:shadow-lg transition-all hover:-translate-y-1 bg-white text-center group cursor-pointer">
                  <CardContent className="p-6">
                    <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full flex items-center justify-center mb-3 mx-auto group-hover:scale-110 transition-transform">
                      <IconComponent className="h-8 w-8 text-blue-600" />
                    </div>
                    <h3 className="font-bold text-gray-900 text-sm">{industry.name}</h3>
                  </CardContent>
                </Card>
              );
            })}
          </div>
          <div className="text-center mt-12">
            <Link to="/industries">
              <Button size="lg" variant="outline" className="border-2 border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white px-8">
                Explore Industries <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">What Our Clients Say</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Don't just take our word for it
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="border-none shadow-lg hover:shadow-xl transition-all bg-gradient-to-br from-blue-50 to-white">
                <CardContent className="p-8">
                  <div className="flex items-center mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <svg key={i} className="w-5 h-5 text-yellow-400 fill-current" viewBox="0 0 20 20">
                        <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                      </svg>
                    ))}
                  </div>
                  <p className="text-gray-700 mb-6 italic">"{testimonial.content}"</p>
                  <div className="flex items-center">
                    <img src={testimonial.image} alt={testimonial.name} className="w-12 h-12 rounded-full object-cover mr-4" />
                    <div>
                      <div className="font-bold text-gray-900">{testimonial.name}</div>
                      <div className="text-sm text-gray-600">{testimonial.position}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 via-blue-700 to-purple-700 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full filter blur-3xl"></div>
        </div>
        <div className="container mx-auto px-4 text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">Ready to Transform Your Business?</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto text-blue-100">
            Let's discuss how we can help you achieve your digital transformation goals
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-white text-blue-600 hover:bg-gray-100 text-lg px-8 py-6 shadow-xl">
                Contact Us Today <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/resources">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-blue-600 text-lg px-8 py-6">
                View Resources
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;